import { Equipment, EquipmentTypes, FeatureType, isTruckOwnershipType, TruckDraft, TruckOwnershipType } from '@numeo/types-core'
import { FormikProps, useFormik } from 'formik'
import * as Yup from 'yup'
import { useEffect, useMemo } from 'react'
import { EquipmentMultiSelect } from '../EquipmentMultiSelect'
import { useAuth } from 'hooks/auth'
import { isFeatureEnabled } from 'utils/feature-checker'
import { Input, Select } from 'components/ds2'
import { BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS } from '@/features/box-truck-loads/utils/format'
import { OwnershipTypeSelect } from './OwnershipTypeSelect'
import { AutofillField } from '@/features/box-truck-loads/components/documentAutofill/AutofillField'

interface BasicInformationProps {
    /** A saved vehicle OR a draft — see `TruckDraft`. The id is only read to decide whether this is a
     *  create, which is exactly what a draft answers. */
    truckData: TruckDraft
    onUpdate: (field: string, value: string | number | null | boolean | Equipment) => void
    /** Optional, and the component already guards for its absence. A caller that never validates through
     *  it — the Box Truck claim form, which cannot demand a plate the driver never submits — should not have
     *  to invent an empty object and assert it is a Formik instance. */
    formRef?: React.MutableRefObject<FormikProps<TruckBasicInformation>>
    boxTruckMode?: boolean
    onBoxTruckEquipmentChange?: (vehicleType: string) => void
}

export interface TruckBasicInformation {
    mainInfo: string
    truckId: string
    vin: string
    unitNumber?: string
    equipment: Equipment
    length?: number | string
    weight?: number | string
    fullPartial?: 'BOTH' | 'FULL' | 'PARTIAL'
    ppDeviceId?: string
    tankCapacityGallons?: number | string
    mpg?: number | string
    minReservePercent?: number | string
    ownershipType?: TruckOwnershipType | null
    make?: string
    model?: string
    year?: number | string
    licensePlate?: string
    state?: string
}

const optionalTruckNumber = Yup.number()
    .nullable()
    .transform((value: number | null, originalValue: unknown) => (typeof originalValue === 'string' && originalValue.trim() === '' ? null : value))

const getValidationSchema = (isCreatingTruck: boolean, boxTruckMode: boolean) =>
    Yup.object().shape({
        mainInfo: boxTruckMode ? Yup.string() : Yup.string().required('Truck name is required'),
        // Plate numbers and unit labels carry punctuation — "ISOLA-789", "TX-1234" — and the alphanumeric-only
        // rule that used to sit here rejected every one of them. Nothing downstream depends on the shape:
        // there is no server-side counterpart, and `truckId` is a free-text label the operator chooses.
        truckId: boxTruckMode ? Yup.string() : Yup.string().required('Truck plate number is required'),
        vin: isCreatingTruck ? Yup.string().trim().required('VIN is required') : Yup.string().trim(),
        length: boxTruckMode ? Yup.mixed() : optionalTruckNumber,
        weight: boxTruckMode ? Yup.mixed() : optionalTruckNumber,
        equipment: Yup.object()
            .shape({
                type: Yup.string().oneOf(['types', 'classes']).required(),
                values: Yup.array().of(Yup.string()).min(1, 'Equipment is required'),
            })
            .required('Equipment is required'),
    })

export const BasicInformation: React.FC<BasicInformationProps> = ({ truckData, onUpdate, formRef, boxTruckMode = false, onBoxTruckEquipmentChange }) => {
    const validationSchema = useMemo(() => getValidationSchema(!truckData._id, boxTruckMode), [truckData._id, boxTruckMode])
    const { application } = useAuth()
    const fuelAiEnabled = isFeatureEnabled(application, FeatureType.FuelAI)
    const formik = useFormik<TruckBasicInformation>({
        initialValues: {
            truckId: truckData.truckId || '',
            mainInfo: truckData.mainInfo || '',
            vin: truckData.vin || '',
            unitNumber: truckData.unitNumber || '',
            length: truckData.length,
            weight: truckData.weight,
            equipment: boxTruckMode
                ? { type: EquipmentTypes.TYPES, values: truckData.boxTruck?.vehicleType ? [truckData.boxTruck.vehicleType] : [] }
                : truckData.equipment || { type: EquipmentTypes.CLASSES, values: [] },
            fullPartial: truckData.fullPartial || 'BOTH',
            ppDeviceId: truckData.ppDeviceId || '',
            tankCapacityGallons: truckData.fuelConfig?.tankCapacityGallons ?? '',
            mpg: truckData.fuelConfig?.mpg ?? '',
            minReservePercent: truckData.fuelConfig?.minReservePercent ?? '',
            ownershipType: isTruckOwnershipType(truckData.ownershipType) ? truckData.ownershipType : undefined,
            make: truckData.make || '',
            model: truckData.model || '',
            // `null` is a year the user cleared: the legacy `truckYear` must not fill it back in (the server unsets both on save).
            year: truckData.year === null ? '' : (truckData.year ?? truckData.truckYear ?? ''),
            licensePlate: truckData.licensePlate || '',
            state: truckData.state || '',
        },
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        validateOnMount: false,
        enableReinitialize: true,
        onSubmit: () => {},
    })
    const equipmentError =
        formik.touched.equipment && formik.errors.equipment
            ? typeof formik.errors.equipment === 'string'
                ? formik.errors.equipment
                : `${boxTruckMode ? 'Vehicle Type' : 'Equipment'} is required`
            : undefined

    useEffect(() => {
        if (!formRef) return
        formRef.current = {
            ...formik,
            validateForm: async () => {
                await formik.setTouched({
                    mainInfo: !boxTruckMode,
                    truckId: !boxTruckMode,
                    vin: true,
                    equipment: { type: true, values: true },
                    length: !boxTruckMode,
                    weight: !boxTruckMode,
                    fullPartial: true,
                })
                return formik.validateForm()
            },
        }
    }, [formik, formRef, boxTruckMode])

    const handleLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === '' ? '' : Number(e.target.value)
        formik.setFieldValue('length', value)
        onUpdate('length', value)
    }

    const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === '' ? '' : Number(e.target.value)
        formik.setFieldValue('weight', value)
        onUpdate('weight', value)
    }

    const handleVehicleTextChange = (field: 'make' | 'model' | 'licensePlate' | 'state', value: string) => {
        formik.setFieldValue(field, value)
        onUpdate(field, value)
    }

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('year', e.target.value)
        // ONE update: the modal and the wizard each clone the truck they rendered with, so a second call in the same event
        // overwrites the first. `year: null` alone is enough — the server unsets the legacy `truckYear` with it (splitYearClear).
        onUpdate('year', e.target.value === '' ? null : Number(e.target.value))
    }

    return (
        <div className="space-y-4 p-3">
            {/* Box Truck uses VIN for identity and keeps the fleet's unit number optional. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <AutofillField field="vin">
                    <Input
                        variant="secondary"
                        label="VIN"
                        value={formik.values.vin || ''}
                        error={formik.touched.vin && formik.errors.vin ? formik.errors.vin : undefined}
                        onChange={(e) => {
                            formik.setFieldValue('vin', e.target.value)
                            onUpdate('vin', e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                        name="vin"
                        data-testid="truck-vin-input"
                    />
                </AutofillField>
                {boxTruckMode && (
                    <Input
                        variant="secondary"
                        label="Unit number (optional)"
                        value={formik.values.unitNumber || ''}
                        onChange={(e) => {
                            formik.setFieldValue('unitNumber', e.target.value)
                            onUpdate('unitNumber', e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                        name="unitNumber"
                        data-testid="truck-unit-number-input"
                    />
                )}
                {!boxTruckMode && (
                    <Input
                        variant="secondary"
                        label="Truck Name"
                        value={formik.values.mainInfo}
                        error={formik.touched.mainInfo && formik.errors.mainInfo ? formik.errors.mainInfo : undefined}
                        onChange={(e) => {
                            formik.handleChange(e)
                            onUpdate('mainInfo', e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                        name="mainInfo"
                        data-testid="truck-name-input"
                    />
                )}
            </div>

            {!boxTruckMode && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                        variant="secondary"
                        label="Truck Plate Number"
                        value={formik.values.truckId}
                        error={formik.touched.truckId && formik.errors.truckId ? formik.errors.truckId : undefined}
                        onChange={(e) => {
                            formik.handleChange(e)
                            onUpdate('truckId', e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                        name="truckId"
                        data-testid="truck-plate-input"
                    />
                    <Input
                        variant="secondary"
                        label="Length (ft)"
                        inputType="number"
                        value={formik.values.length ?? ''}
                        error={formik.touched.length && formik.errors.length ? formik.errors.length : undefined}
                        onChange={handleLengthChange}
                        onBlur={formik.handleBlur}
                        name="length"
                        data-testid="length-input"
                    />
                </div>
            )}

            {!boxTruckMode && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                        variant="secondary"
                        label="Make"
                        value={formik.values.make || ''}
                        onChange={(e) => handleVehicleTextChange('make', e.target.value)}
                        name="make"
                        data-testid="truck-make-input"
                    />
                    <Input
                        variant="secondary"
                        label="Model"
                        value={formik.values.model || ''}
                        onChange={(e) => handleVehicleTextChange('model', e.target.value)}
                        name="model"
                        data-testid="truck-model-input"
                    />
                    <Input
                        variant="secondary"
                        label="Year"
                        inputType="number"
                        value={formik.values.year ?? ''}
                        onChange={handleYearChange}
                        name="year"
                        data-testid="truck-year-input"
                    />
                    <Input
                        variant="secondary"
                        label="License Plate"
                        value={formik.values.licensePlate || ''}
                        onChange={(e) => handleVehicleTextChange('licensePlate', e.target.value)}
                        name="licensePlate"
                        data-testid="truck-license-plate-input"
                    />
                    <Input
                        variant="secondary"
                        label="State"
                        value={formik.values.state || ''}
                        maxLength={2}
                        onChange={(e) => handleVehicleTextChange('state', e.target.value.toUpperCase())}
                        name="state"
                        data-testid="truck-state-input"
                    />
                </div>
            )}

            {/* Row 3: Weight + Equipment */}
            {/* One column once Weight is gone, or the picker sits at half width with an empty cell
                beside it. Row 4 solves the same problem by hiding its container outright; this row
                still has a field to show, so the column count is what changes. */}
            <div className={`grid grid-cols-1 gap-4 ${boxTruckMode ? '' : 'md:grid-cols-2'}`} data-testid="weight-equipment-row">
                {/* A box truck's payload is decided by its cargo dimensions, which the Box Truck vehicle
                    form collects; a tractor's gross weight is a TMS concept that does not describe one.
                    Hidden rather than removed — an imported value stays on the record untouched. */}
                {!boxTruckMode && (
                    <Input
                        variant="secondary"
                        label="Weight (lbs)"
                        inputType="number"
                        value={formik.values.weight ?? ''}
                        error={formik.touched.weight && formik.errors.weight ? formik.errors.weight : undefined}
                        onChange={handleWeightChange}
                        onBlur={formik.handleBlur}
                        name="weight"
                        data-testid="weight-input"
                    />
                )}
                <div>
                    {/* "Equipment" is the TMS word for a trailer class. On a box truck the same control
                        picks what KIND of truck it is, which is what the operator is actually choosing. */}
                    <label className="mb-1.5 block text-sm font-medium text-foreground">{boxTruckMode ? 'Vehicle Type' : 'Equipment'}</label>
                    {boxTruckMode ? (
                        <Select
                            variant="secondary"
                            required
                            value={formik.values.equipment?.values?.[0] ?? ''}
                            options={BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS.map((value) => ({ value, label: value }))}
                            placeholder="Select vehicle type"
                            error={equipmentError}
                            onChange={(value) => {
                                if (!value) return
                                const equipment: Equipment = { type: EquipmentTypes.TYPES, values: value ? [value] : [] }
                                formik.setFieldValue('equipment', equipment)
                                if (onBoxTruckEquipmentChange) onBoxTruckEquipmentChange(value)
                                else onUpdate('equipment', equipment)
                            }}
                        />
                    ) : (
                        <EquipmentMultiSelect
                            formik={formik}
                            onChange={(value) => {
                                formik.setFieldValue('equipment', value)
                                onUpdate('equipment', value)
                            }}
                        />
                    )}
                    {!boxTruckMode && formik.touched.equipment && formik.errors.equipment && (
                        <p className="mt-1 text-xs text-danger">
                            {typeof formik.errors.equipment === 'string' ? formik.errors.equipment : 'Equipment is required'}
                        </p>
                    )}
                </div>
            </div>

            {/* Row 4: Full/Partial + PP Device ID */}
            {/* Both are TMS-only: a box truck operator never splits a load partial, and PrePass is a
                weigh-station transponder for tractors. Hidden, not cleared — an imported value stays. */}
            {!boxTruckMode && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Select
                        variant="secondary"
                        label="Full/Partial Load"
                        data-testid="full-partial-select"
                        value={formik.values.fullPartial || 'BOTH'}
                        onChange={(value) => {
                            formik.setFieldValue('fullPartial', value)
                            onUpdate('fullPartial', value)
                        }}
                        options={[
                            { value: 'BOTH', label: 'BOTH' },
                            { value: 'FULL', label: 'FULL' },
                            { value: 'PARTIAL', label: 'PARTIAL' },
                        ]}
                    />
                    <Input
                        variant="secondary"
                        label="PP Device ID"
                        value={formik.values.ppDeviceId || ''}
                        onChange={(e) => {
                            formik.setFieldValue('ppDeviceId', e.target.value)
                            onUpdate('ppDeviceId', e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                        name="ppDeviceId"
                        placeholder="Enter PrePass device ID"
                        data-testid="pp-device-id-input"
                    />
                </div>
            )}

            {/* Row 5: Ownership — a Box Truck's owner is its contractor (see AssignOwnerContractorDropdown), not this type */}
            {!boxTruckMode && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <OwnershipTypeSelect
                        value={formik.values.ownershipType}
                        onChange={(value) => {
                            formik.setFieldValue('ownershipType', value)
                            onUpdate('ownershipType', value)
                        }}
                    />
                </div>
            )}

            {/* Row 6: Fuel AI fields */}
            {fuelAiEnabled && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Input
                        variant="secondary"
                        label="Tank Capacity (gal)"
                        inputType="number"
                        value={formik.values.tankCapacityGallons ?? ''}
                        onChange={(e) => {
                            const value = e.target.value === '' ? '' : Number(e.target.value)
                            formik.setFieldValue('tankCapacityGallons', value)
                            onUpdate('fuelConfig.tankCapacityGallons', value === '' ? null : value)
                        }}
                        onBlur={formik.handleBlur}
                        name="tankCapacityGallons"
                        placeholder="e.g. 150"
                        data-testid="tank-capacity-input"
                    />
                    <Input
                        variant="secondary"
                        label="MPG"
                        inputType="number"
                        value={formik.values.mpg ?? ''}
                        onChange={(e) => {
                            const value = e.target.value === '' ? '' : Number(e.target.value)
                            formik.setFieldValue('mpg', value)
                            onUpdate('fuelConfig.mpg', value === '' ? null : value)
                        }}
                        onBlur={formik.handleBlur}
                        name="mpg"
                        placeholder="e.g. 6.5"
                        data-testid="mpg-input"
                    />
                    <Input
                        variant="secondary"
                        label="Min Reserve (%)"
                        inputType="number"
                        value={formik.values.minReservePercent ?? ''}
                        onChange={(e) => {
                            const value = e.target.value === '' ? '' : Number(e.target.value)
                            formik.setFieldValue('minReservePercent', value)
                            onUpdate('fuelConfig.minReservePercent', value === '' ? null : value)
                        }}
                        onBlur={formik.handleBlur}
                        name="minReservePercent"
                        placeholder="e.g. 10"
                        data-testid="min-reserve-input"
                    />
                </div>
            )}
        </div>
    )
}
